import React, { useMemo, useState, useRef } from "react";
import UIDataTable from "../UIDataTable/UIDataTable";
import Card from "@mui/material/Card";
import {
  CardContent,
  Grid,
  Button,
  Popover,
  List,
  ListItem,
  Checkbox,
  ListItemText,
  Stack,
  Typography,
  InputAdornment,
} from "@mui/material";
import MDBox from "../MDBox";
import { formatTimeWith } from "utilities/times";
import DialogObservationDetail from "../DialogObservationDetail/DialogObservationDetail";
import PageSize from "../UIDataTable/PageSize";
import { ListTypeObservation } from "constant/TypeObservation";
import { TableUtil } from "utilities/tableUtil";
import { DateRangePicker } from "react-date-range";
import SearchIcon from "@mui/icons-material/Search";
import CustomEyeIcon from "components/icons/CustomEyeIcon";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";

import moment from "moment";
import { enUS } from "date-fns/locale";
import MDInput from "components/MDInput";

const TabObservation = (props) => {
  const {
    isLoading,
    rows,
    currentPage,
    pageSize,
    totalPage,
    totalElement,
    filter,
    onChangePage,
    onChangePageSize,
    onCompletedSelectRangeDate,
    onCompletedSelectObservationType,
    // onSearchByKeyword,
    detail,
    detailLoading,
    onOpenObservationDetail,
  } = props;

  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [typeAnchorEl, setTypeAnchorEl] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [typeOptions, setTypeOptions] = useState(ListTypeObservation);
  const [isTypeLoading, setIsTypeLoading] = useState(false);
  const initialSelectedTypes = useRef([]);
  const [tempRange, setTempRange] = useState({
    startDate: filter.date?.[0] || null,
    endDate: filter.date?.[1] || null,
  });
  const [selectionStep, setSelectionStep] = useState(0);

  const columns = useMemo(() => [
    {
      id: "observationName",
      label: "Observation Name",
      minWidth: 200,
      styleCell: {
        ...TableUtil.styleCell,
        color: "#324054",
        fontSize: "16px",
        fontStyle: "normal",
        fontWeight: 400,
      },
      styleHeader: {
        color: "#172B4D",
        fontStyle: "normal",
        fontWeight: 500,
        fontFamily: "Lexend",
      },
    },
    {
      id: "observationType",
      label: "Observation Type",
      minWidth: 188,
      styleCell: {
        ...TableUtil.styleCell,
        color: "#324054",
        fontSize: "16px",
        fontWeight: 400,
      },
      styleHeader: {
        color: "#172B4D",
        fontStyle: "normal",
        fontWeight: 500,
        fontFamily: "Lexend",
      },
      format: (value) => (value ? value.toUpperCase() : value),
    },
    {
      id: "observationDate",
      label: "Date of observation",
      minWidth: 230,
      styleCell: {
        ...TableUtil.styleCell,
        color: "#324054",
        fontSize: "16px",
        fontWeight: 400,
      },
      styleHeader: {
        color: "#172B4D",
        fontStyle: "normal",
        fontWeight: 500,
        fontFamily: "Lexend",
      },
      format: (value) => formatTimeWith(value, "DD-MMM-YYYY"),
      align: "center",
    },
    {
      id: "observationCode",
      label: "Observation Code",
      minWidth: 250,
      styleCell: {
        ...TableUtil.styleCell,
        color: "#324054",
        fontSize: "16px",
        fontWeight: 400,
      },
      styleHeader: {
        color: "#172B4D",
        fontStyle: "normal",
        fontWeight: 500,
        fontFamily: "Lexend",
      },
    },
    {
      id: "action",
      label: "Action",
      minWidth: 320,
      styleHeader: {
        color: "#172B4D",
        fontStyle: "normal",
        fontWeight: 500,
        fontFamily: "Lexend",
      },
    },
  ]);

  // Format date range for button display
  const formatDateRange = () => {
    const [start, end] = filter.date || [null, null];
    if (!start || !end) return "Select date range";
    //change DD-MM-YYYY to 01 Jan 2024
    /* return `${moment(start).format("DD-MM-YYYY")} -> ${moment(end).format(
      "DD-MM-YYYY"
    )}`; */
    return `${moment(start).format("DD MMM YYYY")} -> ${moment(end).format(
      "DD MMMM YYYY"
    )}`;
  };

  // Handle date range selection
  const handleRangeChange = ({ selection }) => {
    const { startDate, endDate } = selection;
    const newStep = selectionStep + 1;
    setTempRange({ startDate, endDate });
    setSelectionStep(newStep);
    // Submit if both dates are selected and second selection is made
    if (startDate && endDate && newStep >= 2) {
      onCompletedSelectRangeDate([startDate, endDate]);
      setDateAnchorEl(null);
      setSelectionStep(0); // Reset for next selection
    }
  };

  // Handle date range confirmation
  const handleDateConfirm = () => {
    if (tempRange.startDate && tempRange.endDate) {
      onCompletedSelectRangeDate([tempRange.startDate, tempRange.endDate]);
      setDateAnchorEl(null);
    }
  };

  // Date range state for DateRangePicker
  const dateRangeState = [
    {
      startDate: tempRange.startDate,
      endDate: tempRange.endDate,
      key: "selection",
    },
  ];

  // Fetch observation types
  const fetchTypeOptions = async () => {
    setIsTypeLoading(true);
    try {
      // Replace with your actual API call
      await new Promise((resolve) => setTimeout(resolve, 500)); // Mock delay
      // Example: const response = await axios.get("/api/observation-types");
      // setTypeOptions(response.data); // Assume [{ value: string, label: string }, ...]
      setTypeOptions(ListTypeObservation); // Placeholder
    } catch (error) {
      console.error("Failed to fetch observation types:", error);
    } finally {
      setIsTypeLoading(false);
    }
  };

  // Format observation types for button display
  const formatTypeDisplay = () => {
    if (selectedTypes.length === 0) return "Select observation type";
    return typeOptions
      .filter((item) => selectedTypes.includes(item.value))
      .map((item) => item.label)
      .join(", ");
  };

  // Handle observation type selection
  const handleTypeToggle = (value) => () => {
    // Fetch options on first interaction if not loaded
    if (typeOptions.length === 0 && !isTypeLoading) {
      fetchTypeOptions();
    }
    const newSelected = selectedTypes.includes(value)
      ? selectedTypes.filter((item) => item !== value)
      : [...selectedTypes, value];
    setSelectedTypes(newSelected);
    onCompletedSelectObservationType(newSelected);
  };

  // Handle clear observation types
  const handleTypeClear = () => {
    setSelectedTypes([]);
    onCompletedSelectObservationType([]);
    initialSelectedTypes.current = [];
    setTypeAnchorEl(null);
  };

  // Handle type popover close
  const handleTypeClose = () => {
    setTypeAnchorEl(null);
    if (
      JSON.stringify(selectedTypes) !==
      JSON.stringify(initialSelectedTypes.current)
    ) {
      onCompletedSelectObservationType(selectedTypes);
    }
  };

  // Handle date popover open
  const handleDateOpen = () => {
    // Reset tempRange to current filter.date when opening
    setTempRange({
      startDate: filter.date?.[0] || null,
      endDate: filter.date?.[1] || null,
    });
  };

  const propertiesUIDataTable = {
    isLoading,
    columns,
    rows,
    currentPage,
    pageSize,
    totalPage,
    totalElement,
    onChangePage,
    onChangePageSize,
    customTableHeader: (
      <Grid
        container
        spacing={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={({ breakpoints }) => ({
          marginBottom: "8px",
          [breakpoints.down("lg")]: {
            flexDirection: "column",
          },
        })}
      >
        <Grid
          item
          display="flex"
          justifyContent="flex-start"
          sx={({ breakpoints }) => ({
            gap: 1,
            [breakpoints.down("lg")]: {
              flexWrap: "wrap",
              gap: 1,
            },
          })}
        >
          <MDBox className="observation-date-range-picker">
            <Stack direction="row" spacing={1} alignItems="center">
              <MDBox
                className="date-range-button btn-filter"
                variant="outlined"
                onClick={(event) => {
                  setDateAnchorEl(event.currentTarget);
                  handleDateOpen();
                }}
              >
                {formatDateRange()}
              </MDBox>
              <Popover
                open={Boolean(dateAnchorEl)}
                anchorEl={dateAnchorEl}
                onClose={() => setDateAnchorEl(null)}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <Stack className="tab-observation-popover-wrapper-filter-box">
                  <DateRangePicker
                    ranges={dateRangeState}
                    onChange={handleRangeChange}
                    showDateDisplay={true}
                    dateDisplayFormat="dd MMM yyyy"
                    editableDateInputs={true}
                    inputRanges={[]}
                    locale={enUS}
                    // rangeColors={["#1976d2"]}
                    className="rdr-custom"
                  />
                </Stack>
              </Popover>
            </Stack>
          </MDBox>
          <MDBox className="observation-select-type-container">
            <MDBox
              className="select-type-button btn-filter"
              variant="outlined"
              onClick={(event) => setTypeAnchorEl(event.currentTarget)}
            >
              {formatTypeDisplay()}
            </MDBox>
            <Popover
              open={Boolean(typeAnchorEl)}
              anchorEl={typeAnchorEl}
              onClose={handleTypeClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <Stack className="tab-observation-popover-wrapper-filter-box is-list">
                {isTypeLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading...
                  </Typography>
                ) : typeOptions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No options available
                  </Typography>
                ) : (
                  <List>
                    {typeOptions.map((item) => (
                      <ListItem key={item.value} dense>
                        <Checkbox
                          checked={selectedTypes.includes(item.value)}
                          onChange={handleTypeToggle(item.value)}
                        />
                        <ListItemText primary={item.label} />
                      </ListItem>
                    ))}
                  </List>
                )}
                <Button
                  variant="text"
                  size="small"
                  onClick={handleTypeClear}
                  sx={{ mt: 1, textTransform: "none" }}
                >
                  Clear
                </Button>
              </Stack>
            </Popover>
          </MDBox>
          {/* <MDBox className="filter-separator">
            <MDInput
              className="input input-search [&_input]:h-[1.2rem] w-full"
              placeholder="Search by observation name"
              name="keyword"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className="icon-search" />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => onSearchByKeyword?.(e.target.value)}
            />
          </MDBox> */}
          <MDBox className="page-size-select-container">
            <PageSize pageSize={pageSize} onChangePageSize={onChangePageSize} />
          </MDBox>
        </Grid>
      </Grid>
    ),
    customActions: (row) => (
      <Stack direction="row" spacing={2}>
        <Button
          className="action-button view-details"
          onClick={async () => {
            await onOpenObservationDetail(row.id, row.isOnline);
            setIsOpenDialog(true);
          }}
        >
          <CustomEyeIcon />
          View details
        </Button>
        <Button
          className="action-button verify"
          onClick={() => {
            const url =
              "https://docs.google.com/forms/d/e/1FAIpQLSdXiEabBBVG6LpT17qTyqT3xHpfCjDyER8BNJPkqVqfcyf93g/viewform";
            window.open(url, "_blank");
          }}
        >
          <VerifiedUserIcon />
          Verify
        </Button>
      </Stack>
    ),
  };

  return (
    <Card className="observation-tab-card">
      <CardContent className="observation-tab-card-content">
        <UIDataTable {...propertiesUIDataTable} />
        <DialogObservationDetail
          isOpen={isOpenDialog}
          detail={detail}
          detailLoading={detailLoading}
          onClose={() => setIsOpenDialog(false)}
        />
      </CardContent>
    </Card>
  );
};

export default TabObservation;
